/**
 * IntakeFormV2 — schema-driven intake form.
 *
 * Parallel to the existing IntakeForm.tsx. Both routes remain active during
 * the parallel-run period:
 *   - /matters/:id/intake       → IntakeForm (v1, hard-coded TSX sections)
 *   - /matters/:id/intake-v2    → IntakeFormV2 (this file, schema-driven)
 *
 * Architecture:
 *   1. useMatter()         — resolves current matter + contact context
 *   2. pickFormId()        — maps visa_subclass + isSponsor → form_id
 *   3. loadSchema()        — loads the FormSchema JSON (Vite bundled)
 *   4. Bootstrap           — find/create intake_form row; load saved state
 *   5. Cross-form prefill  — merge sponsor/applicant context as read-only
 *   6. SchemaRenderer      — renders section-by-section inside a sidebar layout
 *   7. Autosave            — 1500ms debounce, writes to intake_field_data
 *   8. Submit              — validate + transition matter status
 *   9. PDF download        — POST /api/render-pdf, download blob
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FocusEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Save,
  Info,
  CircleAlert,
  Download,
  Loader2,
} from 'lucide-react';

import { supabase } from '../lib/supabase';
import { upsertIntakeFieldData, type IntakeFieldRow } from '../lib/intakeFieldWrites';
import { useAuth } from '../hooks/useAuth';
import { useMatter } from '../hooks/useMatter';
import { SchemaRenderer, UploadTargetProvider } from '../lib/schema-renderer';
import type { FormSchema } from '../lib/schema-renderer';
import type { FieldDef } from '../lib/schema-renderer/types';
import { resolveLabel } from '../lib/schema-renderer/utils';
import { useGuidance } from '../hooks/useGuidance';

import {
  loadSchema,
  pickFormId,
  hasSponsorForm,
  sponsorRoleNoun,
  formStateToRows,
  rowsToFormState,
  expandFormState,
  mergeReadOnlyContext,
  validateForm,
  computeSectionProgress,
  computeOwnFieldProgress,
  isContextOnlySection,
  loadApplicantContextForSponsor,
  loadSponsorContextForApplicant,
  stripClientHidden,
  hiddenSectionIndices,
  groupSectionsByGate,
  gateProgressPct,
  applyDerivedAnswers,
  sectionBadge,
  groupErrorsBySection,
  UNSCOPED_ERRORS,
} from '../lib/schema-runtime';
import { seedAgentPrefill } from '../lib/agentPrefill';
import { deriveFormCodeVersion, SUPPLEMENTARY_FORM_CODES_SQL } from '../lib/formCodes';
import { buildRelationshipStatementDraft, relationshipStatementDocHtml } from '../lib/relationshipStatement';
import type { FormState, SectionProgress } from '../lib/schema-runtime';
import { getDeFactoEvidence } from '../lib/deFactoEligibility';
import { applyMatterRelationshipStatus } from '../lib/relationshipBasis';
import { computeCrossFieldWarnings } from '../lib/crossFieldWarnings';
import type { CrossFieldWarning } from '../lib/crossFieldWarnings';
import { createSerializedWriteQueue } from '../lib/serializedWriteQueue';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 1500;

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type SubmitStatus = 'idle' | 'submitting' | 'submitted' | 'error';

/**
 * Do two progress arrays say the same thing to the client? The nav badges are
 * driven entirely by these five values, so anything else changing is invisible.
 * Used to keep a per-keystroke recompute from re-rendering the whole form when
 * the numbers have not actually moved.
 */
function sameSectionProgress(a: SectionProgress[], b: SectionProgress[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((p, i) => {
    const q = b[i];
    return (
      p.section_id === q.section_id &&
      p.complete === q.complete &&
      p.touched === q.touched &&
      p.missingRequired === q.missingRequired &&
      p.totalRequired === q.totalRequired
    );
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function IntakeFormV2() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { activeMatter, contact, isStaff, impersonatedMatterId, impersonatedRole, effectiveContactId } =
    useMatter();

  // Which of the two forms is open: stage 1 = pre-lodgement, 2 = post-lodgement.
  const stage: 1 | 2 = searchParams.get('stage') === '2' ? 2 : 1;

  // Audience for label/help resolution. `isStaff` is true only when the viewer
  // is genuinely staff and not impersonating; staff sees staff-facing wording,
  // every client view (real client or staff impersonating a client) sees the
  // client-facing wording. Fields without audience copy render identically.
  const audience: 'client' | 'staff' = isStaff ? 'staff' : 'client';

  // --- Schema loading ---------------------------------------------------------
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  // --- Bootstrap state --------------------------------------------------------
  const [intakeFormId, setIntakeFormId] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);
  // Server-verified one-way stage latch (Phase 3). Loaded from the DB, set by
  // the /api/latch-stage endpoint on submit — the source of truth for the
  // stage-2 gate (replaces the interim computed-completion check).
  const [stage1LatchedAt, setStage1LatchedAt] = useState<string | null>(null);

  // --- Save / submit state ----------------------------------------------------
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitErrors, setSubmitErrors] = useState<Record<string, string>>({});
  const [deFactoBlock, setDeFactoBlock] = useState<string | null>(null);

  // --- Navigation state -------------------------------------------------------
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  // --- Cross-form read-only context -------------------------------------------
  const [, setCrossFormContext] = useState<FormState>({});

  // Resolve sponsor vs applicant role
  const isSponsor = impersonatedMatterId
    ? impersonatedRole === 'sponsor'
    : contact?.contact_type === 'sponsor';

  // BUG-1 (Jane 2026-07-23): impersonation preview is strictly read-only.
  const isPreview = !!impersonatedMatterId;

  // Derive form_id from matter context
  const formId = useMemo(() => {
    if (!activeMatter) return null;
    return pickFormId(
      activeMatter.visa_subclass,
      isSponsor,
      activeMatter.variant,
    );
  }, [activeMatter, isSponsor]);

  // Load schema whenever form_id changes
  useEffect(() => {
    if (!formId) return;
    setSchema(null);
    setSchemaError(null);
    loadSchema(formId)
      .then(setSchema)
      .catch((err: unknown) => {
        setSchemaError(err instanceof Error ? err.message : 'Failed to load schema.');
      });
  }, [formId]);

  // react-hook-form — uses flat canonical_name keys
  const methods = useForm<FormState>({
    defaultValues: {},
    mode: 'onBlur',
  });
  const { watch, getValues, reset } = methods;

  // --- Bootstrap: find or create intake_form, load saved values ---------------
  // Depend on stable primitives (IDs, primitives) rather than object references
  // from useMatter() / useAuth(), which get new identities on every render of
  // the provider and would otherwise refire the bootstrap many times.
  const userId = user?.id ?? null;
  const matterId = activeMatter?.id ?? null;
  const visaSubclass = activeMatter?.visa_subclass ?? null;
  // Matter-level relationship status (R5): the single source of truth for the
  // applicant/sponsor relationship-status answers on 820/309. Injected after
  // hydration and re-applied on every save (see applyMatterRelationshipStatus)
  // so a stale stored blob can never show or persist a different value.
  const matterRelationshipStatus = activeMatter?.relationship_status ?? null;

  // Staff opening the Intake tab WITHOUT impersonating are viewing the client's
  // form, not filling one of their own. effectiveContactId is the staff contact
  // here, so bootstrapping against it tried to create a staff-owned intake_forms
  // row — which the DB trigger (forbid_staff_intake_form) rightly rejects, leaving
  // the tab stuck on "Failed to load intake form." Read the applicant's row
  // instead, and never create one on staff's behalf.
  const staffViewingClientForm = isStaff && !impersonatedMatterId;
  const intakeContactId = staffViewingClientForm
    ? (activeMatter?.primary_contact_id ?? null)
    : effectiveContactId;

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!userId || !matterId || !intakeContactId || !formId || !schema) {
        return;
      }

      try {
        // Look for an existing intake_form row (most-recent first), excluding
        // SUPPLEMENTARY rows: the Form 80 tab keeps its own 'F80-applicant'
        // row per contact, which must never shadow the primary intake row.
        // Deliberately NOT pinned to one exact form_code: legacy rows carry
        // historical codes (a prod 801 matter holds only a '47SP-stage2' row;
        // old staging rows use raw form_ids), and the latest-primary-row rule
        // is exactly the pre-Form-80 behaviour for all of them.
        const { data: existing, error: findErr } = await supabase
          .from('intake_forms')
          .select('id, status, last_saved_at, stage1_completed_at')
          .eq('matter_id', matterId)
          .eq('contact_id', intakeContactId)
          .not('form_code', 'in', SUPPLEMENTARY_FORM_CODES_SQL)
          .order('last_saved_at', { ascending: false })
          .limit(1);
        if (findErr) throw findErr;

        let savedFormId = existing?.[0]?.id ?? null;
        if (!cancelled) setStage1LatchedAt(existing?.[0]?.stage1_completed_at ?? null);

        if (!savedFormId && (staffViewingClientForm || isPreview)) {
          // The client has not started their intake. Show an empty form rather
          // than creating a row owned by staff. In preview (BUG-1) we must never
          // create the client's row either.
          if (!cancelled) {
            setIntakeFormId(null);
            setHydrated(true);
          }
          return;
        }

        if (!savedFormId) {
          // Create a new intake_form row.
          const { form_code, form_version } = deriveFormCodeVersion(formId);
          const { data: created, error: insertErr } = await supabase
            .from('intake_forms')
            .insert({
              matter_id: matterId,
              contact_id: intakeContactId,
              form_code,
              form_version,
              status: 'in_progress',
            })
            .select('id')
            .single();
          if (insertErr) throw insertErr;
          savedFormId = created.id;
          // Seed ADI's own authorised-recipient details before the field data is
          // read below, so they hydrate into the form on this very first load.
          // (Sponsor forms reach creation through here, not CreateMatter.)
          await seedAgentPrefill(savedFormId, formId, { variant: activeMatter?.variant ?? null });
        }

        if (cancelled) return;
        setIntakeFormId(savedFormId);

        // Load saved field data.
        const { data: fieldRows, error: fieldsErr } = await supabase
          .from('intake_field_data')
          .select('field_key, field_value')
          .eq('intake_form_id', savedFormId);
        if (fieldsErr) throw fieldsErr;

        if (cancelled) return;

        // Load cross-form context in parallel.
        let ctx: FormState = {};
        if (isSponsor) {
          ctx = await loadApplicantContextForSponsor(matterId, schema);
        } else if (visaSubclass) {
          ctx = await loadSponsorContextForApplicant(matterId, visaSubclass);
        }

        if (cancelled) return;
        setCrossFormContext(ctx);

        // Merge saved data with cross-form context (saved takes precedence),
        // then expand flat dotted rows into the nested shape RHF resolves.
        // Without the expansion, OBJECT values seeded at dotted keys (address
        // blocks) never reach their subfield inputs — RHF's literal flat-key
        // fallback only rescues scalars.
        // The matter-level relationship status is bound LAST, after expansion,
        // so it always wins over the stored blob/dotted answer (R5): the
        // locked display and every status gate evaluate the matter value.
        const savedState = rowsToFormState(fieldRows ?? []);
        const merged = mergeReadOnlyContext(savedState, ctx);
        reset(applyMatterRelationshipStatus(expandFormState(merged), formId, matterRelationshipStatus));
        setHydrated(true);
      } catch (err) {
        if (cancelled) return;
        setBootError(err instanceof Error ? err.message : 'Failed to load intake form.');
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
    // activeMatter?.variant is only used to seed prefill and is already captured
    // through formId, so it is deliberately excluded to avoid re-hydrating the
    // whole form when the variant changes without changing formId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, matterId, visaSubclass, intakeContactId, staffViewingClientForm, isPreview, formId, schema, isSponsor, reset, matterRelationshipStatus]);

  // --- Autosave: debounced on any form change ----------------------------------
  const debounceTimer = useRef<number | null>(null);
  const lastSavedJson = useRef<string>('');
  // Latest watched values that have not yet been confirmed persisted. Read by
  // the beforeunload/pagehide/unmount flush below (R2-F2) so a navigate-away
  // or tab close within the debounce window doesn't silently drop the edit.
  // Purely a "is there unsaved work" signal now (see scheduleFlush) — it is
  // never itself sent as a write payload, so it can't go stale as a payload.
  const pendingValuesRef = useRef<FormState | null>(null);
  // Flips true once the final submit flush + latch have succeeded, so a
  // debounce timer armed just before Submit can't fire a trailing write
  // after intake_forms has already moved past the client-writable window
  // (B8's trailing-403 finding).
  const submittedRef = useRef(false);
  // R1 EXCEPTION 1 (autosave race): rapid sequential answer-changes could see
  // an EARLIER write's network response land AFTER a LATER write's, silently
  // reverting the saved value to a stale one with no visible error. Root
  // cause was two-fold — (a) a write's payload was whatever was current when
  // it was SCHEDULED, not when it actually ran, and (b) nothing stopped two
  // writes from being in flight at once so their responses could arrive out
  // of order. writeQueueRef serialises every flush (debounce, Next, Submit,
  // beforeunload/pagehide, unmount) through createSerializedWriteQueue — at
  // most one upsert is ever in flight, and each queued write reads
  // getValues() fresh at the moment it actually executes, not at schedule
  // time. wasSuperseded (from the queue) is the monotonic guard: a write only
  // gets to claim "this is what's now saved" (updating lastSavedJson /
  // pendingValuesRef) when no newer flush was queued behind it, so a slow,
  // now-superseded write settling late can never stomp the bookkeeping a
  // newer write already set. Race-fix logic lives in serializedWriteQueue.ts
  // (framework-free, unit-tested there) — this ref just holds one instance.
  const writeQueueRef = useRef(createSerializedWriteQueue());

  const flushSave = useCallback(
    async (fid: string, state: FormState) => {
      setSaveStatus('saving');
      try {
        // Mechanical answers the client never sees (e.g. usual country of
        // residence mirrors the residential address country) are derived here,
        // in the single write path, so every save carries them. The matter
        // relationship status is re-applied first (R5): the whole-blob upsert
        // must never persist a status that diverges from the matter value
        // (e.g. a stale blob captured before a staff basis switch).
        const bound = applyMatterRelationshipStatus(state, schema?.form_id, matterRelationshipStatus);
        const rows = formStateToRows(fid, applyDerivedAnswers(bound, schema?.form_id));
        if (rows.length === 0) {
          setSaveStatus('idle');
          return;
        }
        // count: 'exact' asks PostgREST for the affected-row count via the
        // Content-Range header without switching to return=representation
        // (payload is otherwise identical to before). RLS silently filters
        // out rows the caller isn't allowed to write instead of erroring —
        // that upsert then comes back with no `error` at all, so a client
        // whose write was entirely dropped (e.g. the stage-2 RLS window
        // wasn't open yet) saw "Saved" for a save that wrote nothing
        // (R5-D/B9). A count short of what we sent is treated the same as a
        // thrown error.
        const { error, count } = await upsertIntakeFieldData(rows as IntakeFieldRow[]);
        if (error) throw error;
        if (count !== rows.length) {
          throw new Error(
            `Save incomplete: expected ${rows.length} field(s) to save but the server confirmed ${count ?? 0}.`,
          );
        }
        setLastSaved(new Date());
        setSaveStatus('saved');
        window.setTimeout(() => setSaveStatus((s) => (s === 'saved' ? 'idle' : s)), 2000);
      } catch (err) {
        console.error('IntakeFormV2 autosave failed', err);
        setSaveStatus('error');
      }
    },
    [schema, matterRelationshipStatus],
  );

  /**
   * Enqueue a flush of the CURRENT RHF state for `fid`. Every write path in
   * this component (debounce timer, Next, Submit, beforeunload/pagehide,
   * unmount) goes through this — never call flushSave directly — so writes
   * are strictly serialised and each one sends whatever is truly latest at
   * the moment it runs. Returns the queued promise so a caller that must
   * know the write has landed (Next, Submit) can still await it.
   */
  const scheduleFlush = useCallback(
    async (fid: string): Promise<void> => {
      const result = await writeQueueRef.current.schedule(async () => {
        if (submittedRef.current) return null;
        // Fresh read at execution time, not at the time this flush was
        // queued — a write that waited behind an earlier one still sends the
        // truly-current state once its turn comes up.
        const latest = getValues() as FormState;
        const json = JSON.stringify(latest);
        if (json === lastSavedJson.current) return null;
        await flushSave(fid, latest);
        return json;
      });
      // Only the most-recently-queued flush may update the "what's saved"
      // bookkeeping — a superseded call (a newer flush was queued behind it)
      // finally resolving must not overwrite it with stale state.
      if (!result.wasSuperseded && result.value) {
        lastSavedJson.current = result.value;
        pendingValuesRef.current = null;
      }
    },
    [flushSave, getValues],
  );

  useEffect(() => {
    if (!intakeFormId || !hydrated) return;

    const subscription = watch((values) => {
      if (submittedRef.current) return;
      pendingValuesRef.current = values as FormState;
      if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
      debounceTimer.current = window.setTimeout(() => {
        if (submittedRef.current) return;
        void scheduleFlush(intakeFormId);
      }, DEBOUNCE_MS);
    });

    return () => {
      subscription.unsubscribe();
      if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
      // Route-change / unmount flush (R2-F2): this component unmounts on
      // every internal navigation away from the intake route (tabs, matter
      // list, dashboard, etc — there's no shared layout keeping it alive),
      // so the debounce timer's cleanup above would otherwise discard a
      // still-pending edit with no save and no warning. Fire a best-effort
      // final write with whatever was last typed. Cleanup functions can't be
      // awaited, so this is fire-and-forget; the beforeunload/pagehide
      // handler below covers the browser-level equivalent (closing the tab).
      if (!submittedRef.current && pendingValuesRef.current) {
        void scheduleFlush(intakeFormId);
      }
    };
  }, [watch, intakeFormId, hydrated, isPreview, scheduleFlush]);

  // Browser-level navigate-away (tab close, refresh, address-bar navigation).
  // Unlike the route-change cleanup above, we can't rely on React unmounting
  // in time, and the fetch triggered here has no guarantee of completing
  // before the page tears down. So: fire the flush AND fall back to the
  // browser's native "leave site?" prompt, which pauses the unload long
  // enough for the write to land (or lets the user cancel and stay).
  useEffect(() => {
    const hasPendingEdit = () => {
      if (submittedRef.current || !intakeFormId || !pendingValuesRef.current) return false;
      return JSON.stringify(pendingValuesRef.current) !== lastSavedJson.current;
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasPendingEdit() || !intakeFormId) return;
      void scheduleFlush(intakeFormId);
      event.preventDefault();
      event.returnValue = '';
    };

    const handlePageHide = () => {
      if (!hasPendingEdit() || !intakeFormId) return;
      void scheduleFlush(intakeFormId);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [intakeFormId, isPreview, scheduleFlush]);

  // --- Two-stage split: this form shows ONLY the current stage's sections -----
  // Stage 1 = pre-lodgement form, Stage 2 = post-lodgement form. Staff see the
  // client_hidden sections (e.g. life_in_australia); clients never do.
  const stageLabel = stage === 1 ? 'Pre-lodgement intake' : 'Post-lodgement intake';

  // Only the partner-visa forms are genuinely two-stage. The single-stage forms
  // (1418, 1149, 1419, 147, 1006) carry no stage-2 sections at all, so offering a
  // locked "Post-lodgement" tab promises a form that can never unlock.
  const hasStage2 = useMemo(
    () => (schema?.sections ?? []).some((s) => s.stage === 2),
    [schema],
  );

  const stageSchema = useMemo(() => {
    if (!schema) return null;
    const staged = schema.sections.filter((s) => (s.stage ?? 1) === stage);
    // Staff see everything, including the staff-only blocks (life_in_australia,
    // and the authorised-recipient details we seed with ADI's own agent info).
    // Clients see neither those sections NOR staff-only subsections inside their
    // own sections. stripClientHidden is shared with the server-side stage latch.
    const secs = (isStaff ? staged : stripClientHidden(staged)).sort((a, b) => a.order - b.order);
    return { ...schema, sections: secs };
  }, [schema, stage, isStaff]);

  // Reset to the first section whenever the stage changes.
  useEffect(() => {
    setCurrentSectionIndex(0);
  }, [stage]);

  // --- Section progress (scoped to the current stage) -------------------------
  // RC-1 (820 sweep D-03/D-05/D-39): this was memoised on `lastSaved`, so the
  // badges only recomputed after an autosave round-trip. Between a keystroke and
  // the next save the sidebar showed stale counts, which is what made the
  // percentages appear to move the wrong way and the counts oscillate. It also
  // froze the badges outright in preview, where `setLastSaved` never fires.
  // Recomputed via a watch SUBSCRIPTION (not a render subscription) exactly like
  // the section-gate recompute below; the equality guard means a keystroke that
  // does not change any count causes no re-render at all.
  const [sectionProgress, setSectionProgress] = useState<SectionProgress[]>([]);
  useEffect(() => {
    if (!stageSchema || !hydrated) {
      setSectionProgress([]);
      return;
    }
    const recompute = () => {
      const next = computeSectionProgress(stageSchema, getValues() as FormState);
      setSectionProgress((prev) => (sameSectionProgress(prev, next) ? prev : next));
    };
    recompute();
    const sub = watch(() => recompute());
    return () => sub.unsubscribe();
  }, [stageSchema, hydrated, watch, getValues]);

  // Submit errors bucketed by the section each one belongs to (Jane 2026-07-21:
  // errors render inside their own tab, never as one global banner). Drives the
  // per-section nav badges and the per-section error box below.
  const errorsBySection = useMemo(
    () => (stageSchema ? groupErrorsBySection(stageSchema, submitErrors) : new Map()),
    [stageSchema, submitErrors],
  );

  // Sections for navigation (already stage-filtered + sorted)
  const sections = useMemo(() => stageSchema?.sections ?? [], [stageSchema]);

  // --- Contextual assistant guidance (Phase 1) -------------------------------
  // Publish where the client is (section, active field, current errors) and
  // what's still outstanding into GuidanceContext, so the FAQ assistant can
  // answer page/field-specific questions. PII-safe: labels + section titles
  // only, never field values or the visa subclass. See useGuidance.tsx.
  const { setForm: setGuidance } = useGuidance();

  // canonical_name → human label, for turning error/active-field keys into
  // wording the client actually sees.
  const fieldLabels = useMemo(() => {
    const m = new Map<string, string>();
    if (!stageSchema) return m;
    const formId = stageSchema.form_id ?? schema?.form_id ?? 'default';
    const walk = (fields?: FieldDef[]): void => {
      for (const f of fields ?? []) {
        if (f.canonical_name) m.set(f.canonical_name, resolveLabel(f, formId));
        if (f.entry_schema) walk(f.entry_schema);
      }
    };
    for (const sec of stageSchema.sections ?? []) {
      walk(sec.fields);
      for (const sub of sec.subsections ?? []) walk(sub.fields);
    }
    return m;
  }, [stageSchema, schema?.form_id]);

  const labelForName = useCallback(
    (name: string): string => {
      // Repeater DOM names carry numeric indices (a.children.0.x); the label
      // map is keyed by canonical wildcard form (a.children[*].x).
      const canonical = name.replace(/\.(\d+)(?=\.|$)/g, '[*]');
      return fieldLabels.get(name) ?? fieldLabels.get(canonical) ?? name;
    },
    [fieldLabels],
  );

  // Track the field the client is focused on, so "what do I put here?" resolves.
  const handleFieldFocus = useCallback(
    (e: FocusEvent<HTMLFormElement>) => {
      const name = (e.target as HTMLElement).getAttribute?.('name');
      if (!name) return;
      const cs = sections[currentSectionIndex];
      const error = cs
        ? (errorsBySection.get(cs.section_id) ?? []).find(
            (x: { key: string; message: string }) => x.key === name,
          )?.message
        : undefined;
      setGuidance((prev) => ({ ...(prev ?? {}), activeField: { key: name, label: labelForName(name), error } }));
    },
    [sections, currentSectionIndex, errorsBySection, labelForName, setGuidance],
  );

  // Push section / errors / completeness whenever they change.
  useEffect(() => {
    const cs = sections[currentSectionIndex];
    if (!cs) return;
    const scoped = errorsBySection.get(cs.section_id) ?? [];
    const visibleErrors = scoped.map((e: { key: string; message: string }) => ({
      key: e.key,
      label: labelForName(e.key),
      error: e.message,
    }));
    const incomplete = sectionProgress.filter((p) => !p.complete).map((p) => p.title);
    const total = sectionProgress.length;
    const done = sectionProgress.filter((p) => p.complete).length;
    const percent = total ? Math.round((done / total) * 100) : undefined;
    setGuidance((prev) => ({
      ...(prev ?? {}),
      sectionId: cs.section_id,
      sectionTitle: cs.title,
      stage,
      visibleErrors,
      outstanding: {
        sectionsIncomplete: incomplete,
        documentsMissing: prev?.outstanding?.documentsMissing ?? [],
        percent,
      },
    }));
  }, [sections, currentSectionIndex, errorsBySection, sectionProgress, stage, labelForName, setGuidance]);

  // Clear guidance when the client leaves the intake form.
  useEffect(() => () => setGuidance(null), [setGuidance]);

  // --- Section-level appears_when in the nav (review fix 2026-07-22) ---------
  // SectionRenderer already blanks a gated section's body, and validation /
  // progress / PDF skip it, but the nav and Next/Prev never evaluated
  // section-level gates, so e.g. "Relationship registration" stayed a
  // clickable blank entry for a Married client. This set drives the nav
  // filter and traversal. Recomputed via a watch SUBSCRIPTION (no render
  // subscription, so no per-keystroke re-render of the whole form); state
  // only updates when the set genuinely changes, so a mid-session status
  // change makes the nav entry appear or disappear immediately.
  const [hiddenSectionIdx, setHiddenSectionIdx] = useState<Set<number>>(new Set());
  useEffect(() => {
    const recompute = () => {
      const next = hiddenSectionIndices(sections, getValues() as FormState);
      setHiddenSectionIdx((prev) => {
        if (prev.size === next.size && [...next].every((i) => prev.has(i))) return prev;
        return next;
      });
    };
    recompute();
    const sub = watch(() => recompute());
    return () => sub.unsubscribe();
  }, [sections, watch, getValues]);

  const visibleSectionIndices = useMemo(
    () => sections.map((_, i) => i).filter((i) => !hiddenSectionIdx.has(i)),
    [sections, hiddenSectionIdx],
  );

  // If the section being viewed becomes hidden (e.g. relationship status
  // changed mid-session), move to the nearest visible section.
  useEffect(() => {
    if (sections.length === 0 || !hiddenSectionIdx.has(currentSectionIndex)) return;
    const forward = visibleSectionIndices.find((i) => i > currentSectionIndex);
    const backward = [...visibleSectionIndices].reverse().find((i) => i < currentSectionIndex);
    setCurrentSectionIndex(forward ?? backward ?? 0);
  }, [sections, hiddenSectionIdx, currentSectionIndex, visibleSectionIndices]);

  // Sections whose every field is read-only cross-form context (owned by another
  // form — e.g. the sponsor 40SP "Applicant — Identity (verification)" block).
  // The client can't author these, so they must not count toward the client's
  // progress %, and their "N required missing" hint is meaningless. Kept as a
  // Set so we DON'T alter the full-length sectionProgress array (which stays
  // index-coupled to the nav badges below).
  const contextOnlySectionIds = useMemo(() => {
    if (!stageSchema) return new Set<string>();
    return new Set(
      sections
        .filter((s) => isContextOnlySection(s, stageSchema.form_id))
        .map((s) => s.section_id),
    );
  }, [stageSchema, sections]);

  // Milestone gates (partner 47SP forms; Jane's reorg). Empty for non-gated
  // forms, which keep the flat section nav. Gates group the nav and carry a
  // per-milestone progress badge — they never hide or lock sections.
  const gateGroups = useMemo(() => groupSectionsByGate(sections), [sections]);
  const contextOnlyIndices = useMemo(() => {
    const out = new Set<number>();
    sections.forEach((s, i) => {
      if (contextOnlySectionIds.has(s.section_id)) out.add(i);
    });
    return out;
  }, [sections, contextOnlySectionIds]);

  // Is the pre-lodgement (stage 1) form complete? Gates client access to the
  // post-lodgement form (staff are never gated). Source of truth is the
  // server-verified latch (intake_forms.stage1_completed_at), set by
  // /api/latch-stage on submit — not a live re-compute of form state.
  const stage1Complete = stage1LatchedAt != null;

  // --- Cross-field WARNINGS (non-blocking) ------------------------------------
  // B2's EDGE-01/EDGE-02 findings: marriage-before-relationship-began and an
  // expired passport are silently accepted with no flag. There is no Immi
  // evidence either combination should hard-block submission, so these are
  // deliberately warnings, not validateForm errors — never added to
  // submitErrors, never able to block Next/Submit or the server-side latch.
  // Recomputed on every save so the client sees them as soon as the relevant
  // fields are filled in, not only after attempting to submit.
  // RC-1 again: same stale-memo defect as sectionProgress. Keyed on `lastSaved`
  // these warnings only appeared after an autosave, and never at all in preview.
  // Same watch subscription, same equality guard.
  const [crossFieldWarnings, setCrossFieldWarnings] = useState<CrossFieldWarning[]>([]);
  useEffect(() => {
    if (!hydrated || !schema) {
      setCrossFieldWarnings([]);
      return;
    }
    const recompute = () => {
      const next = computeCrossFieldWarnings(getValues() as FormState, { formId: schema.form_id });
      setCrossFieldWarnings((prev) =>
        prev.length === next.length && prev.every((w, i) => w.id === next[i].id && w.message === next[i].message)
          ? prev
          : next,
      );
    };
    recompute();
    const sub = watch(() => recompute());
    return () => sub.unsubscribe();
  }, [schema, hydrated, watch, getValues]);

  // --- Next-step handler: flush then advance ----------------------------------
  const handleNext = async () => {
    if (intakeFormId) {
      // Routed through scheduleFlush (not a direct flushSave call) so this
      // write is serialised against any debounce-triggered write still in
      // flight, and always sends the truly-current state (see R1 EXCEPTION 1
      // notes above scheduleFlush's definition).
      await scheduleFlush(intakeFormId);
      await supabase
        .from('intake_forms')
        .update({ last_saved_at: new Date().toISOString() })
        .eq('id', intakeFormId);
    }
    // Advance to the next VISIBLE section (skips sections whose section-level
    // appears_when currently evaluates hidden).
    setCurrentSectionIndex((i) => visibleSectionIndices.find((v) => v > i) ?? i);
  };

  // --- Submit handler ---------------------------------------------------------
  const handleSubmit = async () => {
    if (!schema || !intakeFormId) return;

    setSubmitStatus('submitting');
    const state = getValues() as FormState;
    // Validate only the current stage's fields (the other stage's form is separate).
    const { errors } = validateForm(stageSchema ?? schema, state);

    if (Object.keys(errors).length > 0) {
      setSubmitErrors(errors);
      setSubmitStatus('error');
      // Jump to the first section (in visible order) that actually has an
      // error, so the client lands on the tab that needs fixing rather than
      // reading a cross-section list they can't act on from here.
      const grouped = groupErrorsBySection(stageSchema ?? schema, errors);
      const firstErrored = visibleSectionIndices.find((i) =>
        grouped.has(sections[i]?.section_id ?? ''),
      );
      if (firstErrored !== undefined) setCurrentSectionIndex(firstErrored);
      return;
    }

    setSubmitErrors({});

    // De-facto 12-month eligibility gate. Only fires for applicant 47SP forms
    // on the de-facto track. Form data persists either way (autosave already
    // ran), so the user can fix the underlying field and re-submit.
    if (stage === 2 && !isSponsor && activeMatter && activeMatter.variant === 'defacto') {
      // Check whether a state-registered relationship certificate has been
      // uploaded against this matter. We accept any non-deleted document with
      // checklist_item='relationship_certificate' (matches the doc-checklist
      // key in src/lib/documentChecklist.ts) — review status doesn't matter
      // for the gate; the cert's existence is what proves the relationship is
      // registered.
      let hasRegisteredCert = false;
      try {
        const { data: certDocs } = await supabase
          .from('documents')
          .select('id')
          .eq('matter_id', activeMatter.id)
          .eq('checklist_item', 'relationship_certificate')
          .is('deleted_at', null)
          .limit(1);
        hasRegisteredCert = (certDocs ?? []).length > 0;
      } catch {
        // Non-fatal — if the lookup fails, fall back to "no cert" which is
        // the safer (more-blocking) assumption.
        hasRegisteredCert = false;
      }

      const evidence = getDeFactoEvidence({
        variant: activeMatter.variant ?? null,
        dateDeFactoBegan:
          (state['applicant.relationship.date_de_facto_began'] as string | null | undefined) ?? null,
        hasRegisteredRelationshipCert: hasRegisteredCert,
        compellingCircumstances:
          (state['applicant.relationship.compelling_circumstances'] as string | null | undefined) ?? null,
      });

      // Informational banner only — the portal can't decide eligibility from
      // self-reported data. Eli records the basis on the LodgementGate; the
      // DB trigger fn_check_defacto_eligibility_basis enforces sign-off
      // before lodgement. Surface the three paths here so the client sees
      // what evidence is expected, but never block submit.
      const cohabMonths = evidence.evidence.cohabitation.monthsElapsed;
      const cohabSatisfied = cohabMonths !== null && cohabMonths >= 12;
      const certUploaded = evidence.evidence.registered.certificateUploaded;
      const compellingPresent = evidence.evidence.compelling.charCount >= 30;
      if (evidence.applies && !cohabSatisfied && !certUploaded && !compellingPresent) {
        setDeFactoBlock(
          'De facto partner visas need (a) 12+ months cohabitation, (b) a state-registered relationship certificate, or (c) compelling and compassionate circumstances. None of these are confirmed on this matter yet. Eli will need to record one of these before lodgement.',
        );
        // intentional: NO `return` — banner is informational, submit proceeds.
      } else {
        setDeFactoBlock(null);
      }
    } else {
      setDeFactoBlock(null);
    }

    // Final flush so the server sees the same data the client just validated.
    // Routed through scheduleFlush: serialises against any debounce-triggered
    // write still in flight and reads getValues() fresh at the moment it
    // actually runs, so it can never be overtaken by (or itself overtake) an
    // older write left over from the last few keystrokes before Submit.
    await scheduleFlush(intakeFormId);

    // Server-verified one-way latch. The endpoint re-validates this stage from
    // the persisted rows and sets stage{1,2}_completed_at atomically. This is
    // what gates the post-lodgement form, so a failed latch blocks completion.
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('No active session. Please sign in again.');
      const latchUrl = (import.meta.env.VITE_PDF_API_URL ?? '/api/render-pdf').replace(
        '/api/render-pdf',
        '/api/latch-stage',
      );
      const resp = await fetch(latchUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ matter_id: matterId, intake_form_id: intakeFormId, form_id: formId, stage }),
      });
      if (!resp.ok) {
        const detail = (await resp.json().catch(() => ({}))) as { error?: string };
        throw new Error(detail.error ?? `Latch failed (${resp.status})`);
      }
      const result = (await resp.json()) as { at?: string };
      if (stage === 1 && result.at) setStage1LatchedAt(result.at);
    } catch (err) {
      console.error('IntakeFormV2 latch failed', err);
      setSubmitErrors({ _latch: err instanceof Error ? err.message : 'Could not confirm completion. Please try again.' });
      setSubmitStatus('error');
      return;
    }

    // The lifecycle transition (intake_forms status/completed_at and matters
    // awaiting_review) happens INSIDE /api/latch-stage under the service role.
    // The Audit E guard trigger forbids non-staff writes to intake_forms
    // lifecycle columns, so the direct updates that used to live here 403'd
    // for every real client submit (S1, 2026-07-13 regression run).

    // Submit has fully succeeded (final flush + latch both landed). Stop the
    // debounced watcher from firing again: a change made in the seconds
    // before Submit can still have a debounce timer armed at this point, and
    // letting it fire after the latch has moved intake_forms past the
    // client-writable window is exactly B8's trailing-403 finding.
    submittedRef.current = true;
    if (debounceTimer.current) {
      window.clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    pendingValuesRef.current = null;

    setSubmitStatus('submitted');
    navigate('/thank-you');
  };

  // --- PDF download -----------------------------------------------------------
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleDownloadPdf = async () => {
    if (!intakeFormId || !formId) return;
    setPdfLoading(true);
    setPdfError(null);
    try {
      // The PDF endpoint runs as a Vercel serverless function (Chromium can't
      // run on Cloudflare Pages edge). VITE_PDF_API_URL points at it; in dev
      // it can be left unset to use the same-origin /api/render-pdf path
      // served by `vercel dev`.
      const pdfApiUrl = import.meta.env.VITE_PDF_API_URL ?? '/api/render-pdf';

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('No active session. Please sign in again.');

      const response = await fetch(pdfApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          matter_id: activeMatter?.id,
          form_id: formId,
          intake_form_id: intakeFormId,
        }),
      });
      if (!response.ok) {
        throw new Error(`PDF generation failed: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${formId}_${activeMatter?.matter_id ?? activeMatter?.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'PDF download failed.');
    } finally {
      setPdfLoading(false);
    }
  };

  // --- Render states ----------------------------------------------------------

  if (bootError || schemaError) {
    return (
      <div className="card p-6 text-sm text-red-600">
        {bootError ?? schemaError}
      </div>
    );
  }

  // A sponsor on a visa that has no sponsor form (461, 020, 600-tourist). This
  // used to fall through to the 40SP partner-visa sponsorship form and even
  // persist a 40SP intake row against the matter.
  if (isSponsor && visaSubclass && !hasSponsorForm(visaSubclass)) {
    return (
      <div className="card p-6 text-sm text-slate-600">
        There is no separate sponsor form for this visa. Their details are captured on
        the applicant's form.
      </div>
    );
  }

  // Staff opened the Intake tab before the client has started their form. We do
  // not create a row on staff's behalf (see the bootstrap), so there is nothing
  // to show yet.
  if ((staffViewingClientForm || isPreview) && hydrated && !intakeFormId) {
    return (
      <div className="card p-6 text-sm text-slate-600">
        {isSponsor
          ? `The ${sponsorRoleNoun(visaSubclass ?? '')} has not started this form yet.`
          : 'The client has not started their intake form yet.'}
      </div>
    );
  }

  if (!hydrated || !intakeFormId || !schema) {
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" />
        Loading your intake form...
      </div>
    );
  }

  const currentSection = sections[currentSectionIndex];
  // Position and totals count VISIBLE sections only, so a gated-out section
  // (e.g. relationship registration on a Married matter) does not leave holes
  // in the numbering or a phantom entry in the totals.
  const lastVisibleIndex = visibleSectionIndices[visibleSectionIndices.length - 1] ?? sections.length - 1;
  const firstVisibleIndex = visibleSectionIndices[0] ?? 0;
  const visibleSectionCount = visibleSectionIndices.length;
  const currentVisiblePosition = Math.max(1, visibleSectionIndices.indexOf(currentSectionIndex) + 1);
  const isLastSection = currentSectionIndex === lastVisibleIndex;
  const isSubmitted = submitStatus === 'submitted';

  // Overall % counts ONLY the client's own required fields (read-only cross-form
  // context prefilled from a related form is excluded). Uses the shared
  // computeOwnFieldProgress so this header and the dashboard can't disagree, and
  // an untouched form reads 0% (see finding 8).
  const overallProgress = stageSchema
    ? computeOwnFieldProgress(stageSchema, getValues() as FormState)
    : 0;

  const formLabel = schema.form_name;
  const stage2Locked = !isStaff && !stage1Complete;
  const goToStage = (s: 1 | 2) => {
    const next = new URLSearchParams(searchParams);
    if (s === 2) next.set('stage', '2');
    else next.delete('stage');
    setSearchParams(next);
  };

  // A single-stage form has no stage 2 to open — send ?stage=2 back to the form.
  if (stage === 2 && !hasStage2) {
    return <Navigate to={{ search: '' }} replace />;
  }

  // Client cannot open the post-lodgement form until the pre-lodgement form is done.
  if (stage === 2 && stage2Locked) {
    return (
      <div className="max-w-xl mx-auto text-center py-16 px-4">
        <h1 className="text-2xl font-bold text-[#0B2341] mb-2">Post-lodgement intake</h1>
        <p className="text-slate-600 mb-6">
          This form unlocks automatically once you finish the pre-lodgement intake form.
          Complete that first and we will open this next step for you.
        </p>
        <button type="button" className="btn-primary" onClick={() => goToStage(1)}>
          Go to the pre-lodgement form
        </button>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={(e) => e.preventDefault()} onFocusCapture={handleFieldFocus}>
        {/* Two-stage form switcher — only for forms that actually have a stage 2 */}
        {hasStage2 && (
          <div className="mb-4 flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => goToStage(1)}
              className={`px-3 py-1.5 rounded-full font-semibold ${stage === 1 ? 'bg-[#0B2341] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              1. Pre-lodgement
            </button>
            <button
              type="button"
              onClick={() => goToStage(2)}
              disabled={stage2Locked}
              className={`px-3 py-1.5 rounded-full font-semibold ${stage === 2 ? 'bg-[#0B2341] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'} disabled:opacity-50 disabled:cursor-not-allowed`}
              title={stage2Locked ? 'Unlocks after you complete the pre-lodgement form' : undefined}
            >
              2. Post-lodgement {stage2Locked ? '🔒' : ''}
            </button>
          </div>
        )}
        {/* Form identity banner */}
        <div className="mb-4 rounded-lg bg-[#EBF7FD] border border-[#2CABEA]/30 px-4 py-2.5 text-sm flex items-center gap-2 text-[#0B2341]">
          <Info size={14} className="text-[#2CABEA] shrink-0" />
          <span>
            <span className="font-semibold">{stageLabel}</span>
            <span className="text-slate-500 ml-2">· {formLabel} · {visibleSectionCount} sections</span>
          </span>
        </div>

        {/* Mobile sticky step indicator */}
        <div className="xl:hidden sticky top-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-white/95 backdrop-blur border-b border-slate-200 mb-3">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 truncate">
              <span className="text-slate-500">Section</span>{' '}
              <span className="font-semibold">
                {currentVisiblePosition} of {visibleSectionCount}
              </span>
              <span className="text-slate-300 mx-1.5">·</span>
              <span className="font-semibold text-[#0B2341]">{currentSection?.title}</span>
            </span>
            <span className="text-xs font-semibold text-[#2CABEA] shrink-0">
              {overallProgress}%
            </span>
          </div>
        </div>

        <div className="flex flex-col xl:grid xl:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar */}
          <div className="order-2 xl:order-1 xl:sticky xl:top-6 xl:self-start space-y-4">
            {/* Progress card */}
            <div className="card p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#2CABEA]">
                  {stageLabel}
                </span>
                <span className="text-sm font-semibold">{overallProgress}%</span>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full bg-[#2CABEA] transition-all"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mb-1 flex items-center gap-1.5">
                <Save size={11} />
                {renderSaveLabel(saveStatus, lastSaved)}
              </p>
            </div>

            {/* Section navigation (grouped by milestone gate on partner forms) */}
            <nav className="card p-2">
              {(() => {
                const renderItem = (section: (typeof sections)[number], i: number) => {
                  const progress = sectionProgress[i];
                  const isCurrent = i === currentSectionIndex;
                  return (
                    <li key={section.section_id}>
                      <button
                        type="button"
                        onClick={() => setCurrentSectionIndex(i)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-start gap-2.5 transition-colors ${
                          isCurrent
                            ? 'bg-[#EBF7FD] text-[#0B2341]'
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <span className="mt-0.5 flex-shrink-0">
                          <SectionStatusIcon progress={progress} isCurrent={isCurrent} index={i} />
                        </span>
                        <span className="flex-1">
                          <span
                            className={`block font-medium ${isCurrent ? 'text-[#0B2341]' : ''}`}
                          >
                            {section.title}
                          </span>
                          {/* RC-2 (820 sweep D-04/D-16). The rule itself lives in
                              sectionBadge() so it is pinned by tests; this is
                              only how each outcome is painted. */}
                          {(() => {
                            const badge = sectionBadge(
                              progress,
                              contextOnlySectionIds.has(section.section_id),
                            );
                            if (badge.kind === 'not-started') {
                              return <span className="block text-xs text-slate-400 mt-0.5">Not started</span>;
                            }
                            if (badge.kind === 'missing') {
                              return (
                                <span className="block text-xs text-amber-500 mt-0.5">
                                  {badge.count} required field{badge.count !== 1 ? 's' : ''} missing
                                </span>
                              );
                            }
                            return null;
                          })()}
                          {(errorsBySection.get(section.section_id)?.length ?? 0) > 0 && (
                            <span className="block text-xs text-red-500 mt-0.5">
                              {errorsBySection.get(section.section_id)!.length} to fix before submitting
                            </span>
                          )}
                          {section.section_id === 'address_travel_history' &&
                            crossFieldWarnings.some((w) => w.id === 'address-history-gaps') && (
                              <span className="block text-xs text-red-500 mt-0.5">
                                Address history has gaps
                              </span>
                            )}
                        </span>
                      </button>
                    </li>
                  );
                };
                // Sections whose section-level appears_when currently evaluates
                // hidden are omitted from the nav entirely (their body would
                // render blank; validation and progress already skip them).
                if (gateGroups.length === 0) {
                  return (
                    <ol className="space-y-0.5">
                      {sections.map((section, i) =>
                        hiddenSectionIdx.has(i) ? null : renderItem(section, i),
                      )}
                    </ol>
                  );
                }
                return gateGroups.map((group) => {
                  const visibleGroupIndices = group.sectionIndices.filter(
                    (i) => !hiddenSectionIdx.has(i),
                  );
                  if (visibleGroupIndices.length === 0) return null;
                  const pct = gateProgressPct(group, sectionProgress, contextOnlyIndices);
                  return (
                    <div key={group.gate} className="mb-1 last:mb-0">
                      <div className="flex items-center justify-between px-3 pt-2 pb-1">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                          {group.gate}. {group.label}
                        </span>
                        <span
                          className={`text-[11px] font-semibold ${pct === 100 ? 'text-emerald-500' : 'text-[#2CABEA]'}`}
                        >
                          {pct}%
                        </span>
                      </div>
                      <ol className="space-y-0.5">
                        {visibleGroupIndices.map((i) => renderItem(sections[i], i))}
                      </ol>
                    </div>
                  );
                });
              })()}
            </nav>

            {/* PDF download (shown after submission) */}
            {isSubmitted && (
              <div className="card p-4 space-y-3">
                <p className="text-sm font-medium text-slate-700">Form submitted</p>
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  className="btn-secondary w-full text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {pdfLoading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Download size={14} />
                  )}
                  {pdfLoading ? 'Generating PDF...' : 'Download PDF'}
                </button>
                {pdfError && (
                  <p className="text-xs text-red-600">{pdfError}</p>
                )}
              </div>
            )}

            {/* Relationship statement draft (staff-only, partner initial forms).
                Assembles the lodgement-gate answers into ADI's statement
                template structure as an editable Word-compatible draft. */}
            {isStaff && schema && ['47SP_820_onshore_stage1', '47SP_309_offshore_stage1', '47SP_300_PMV'].includes(schema.form_id) && (
              <div className="card p-4 space-y-2">
                <p className="text-sm font-medium text-slate-700">Relationship statement</p>
                <button
                  type="button"
                  onClick={() => {
                    const draft = buildRelationshipStatementDraft(getValues() as FormState);
                    const blob = new Blob([relationshipStatementDocHtml(draft)], { type: 'application/msword' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `Relationship Statement - ${draft.applicantName || 'applicant'}.doc`;
                    a.click();
                    URL.revokeObjectURL(a.href);
                  }}
                  className="btn-secondary w-full text-sm flex items-center justify-center gap-2"
                >
                  <Download size={14} />
                  Download statement draft
                </button>
                <p className="text-xs text-slate-500">
                  Editable draft from the client's answers. Polish it in Word, paste into the branded template, then upload it to the Relationship history statement checklist item.
                </p>
              </div>
            )}
          </div>

          {/* Main content area */}
          <div className="order-1 xl:order-2">
            <div className="card">
              {/* Section header */}
              <div className="section">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">
                  Section {currentVisiblePosition} of {visibleSectionCount}
                </div>
                <h1 className="text-2xl font-bold">{currentSection?.title}</h1>
                {currentSection?.description && (
                  <p className="text-sm text-slate-500 mt-1">{currentSection.description}</p>
                )}
              </div>

              {/* Schema-driven section content */}
              <div className="section">
                {/* Staff may edit any field from any view, including "view as
                    client" (Tzvi, 2026-08-03). This fieldset used to carry
                    disabled={isPreview}, which is what made every field inert. */}
                <fieldset className="contents">
                {currentSection && stageSchema && (
                  matterId && intakeContactId ? (
                    // UploadTargetProvider is what lets FileUploadR fields
                    // actually upload, using the only matter/contact pairing
                    // this component ever knows unambiguously (see intakeContactId
                    // above: the applicant's row for staff viewing without
                    // impersonation, the impersonated contact under
                    // impersonation, else the signed-in client's own contact).
                    // uploaderRole mirrors isSponsor, which is already proven
                    // equivalent to intakeContactId's own contact_type in every
                    // branch above (real client: contact.contact_type itself;
                    // impersonating: impersonatedRole, matching effectiveContactId's
                    // role; staff viewing: forced false, matching the forced
                    // primary_contact_id/applicant lookup).
                    <UploadTargetProvider
                      value={{
                        matterId,
                        contactId: intakeContactId,
                        uploaderRole: isSponsor ? 'sponsor' : 'applicant',
                      }}
                    >
                      <SchemaRenderer
                        key={currentSection.section_id}
                        // MUST be stageSchema, not schema. singleSectionSchema re-looks
                        // the section up by id, so passing the unfiltered schema hands
                        // back the ORIGINAL section with its client_hidden subsections
                        // still attached — and the client sees staff-only blocks. The
                        // section-level filter hid this for a long time (a hidden SECTION
                        // never becomes currentSection), but a hidden SUBSECTION lives
                        // inside a visible section and leaked straight through.
                        schema={singleSectionSchema(stageSchema, currentSection.section_id)}
                        formId={schema.form_id}
                        audience={audience}
                        showLoadingState
                        showSectionHeading={false}
                      />
                    </UploadTargetProvider>
                  ) : (
                    <SchemaRenderer
                      key={currentSection.section_id}
                      schema={singleSectionSchema(stageSchema, currentSection.section_id)}
                      formId={schema.form_id}
                      audience={audience}
                      showLoadingState
                      showSectionHeading={false}
                    />
                  )
                )}
                </fieldset>
              </div>

              {/* Submit errors, scoped to THIS section only (Jane 2026-07-21:
                  never a global banner spanning sections). Errors that could
                  not be mapped to a section (e.g. the submit-level latch
                  failure) show here too so they are never swallowed. */}
              {(() => {
                const currentId = currentSection?.section_id ?? '';
                const scoped = [
                  ...(errorsBySection.get(currentId) ?? []),
                  ...(errorsBySection.get(UNSCOPED_ERRORS) ?? []),
                ];
                if (scoped.length === 0) return null;
                return (
                  <div className="section">
                    <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                      <p className="font-semibold mb-2">
                        Please complete the following required fields before submitting:
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        {scoped.slice(0, 10).map((e) => (
                          <li key={e.key}>{e.message}</li>
                        ))}
                        {scoped.length > 10 && <li>...and {scoped.length - 10} more</li>}
                      </ul>
                    </div>
                  </div>
                );
              })()}

              {/* Cross-field warnings — non-blocking. Never gates Next/Submit;
                  see computeCrossFieldWarnings for the rules (marriage before
                  relationship began, expired passport, PMV intended-marriage
                  window). role="status" (not "alert") because nothing here
                  requires the client to act before continuing. */}
              {crossFieldWarnings.some((w) => w.blocking) && (
                <div className="section">
                  <div
                    role="alert"
                    className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800"
                  >
                    <p className="font-semibold mb-2">Must be fixed before the form can be finished</p>
                    <ul className="list-disc list-inside space-y-1">
                      {crossFieldWarnings.filter((w) => w.blocking).map((w) => (
                        <li key={w.id}>{w.message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {crossFieldWarnings.some((w) => !w.blocking) && (
                <div className="section">
                  <div
                    role="status"
                    className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800"
                  >
                    <p className="font-semibold mb-2">Worth double-checking</p>
                    <ul className="list-disc list-inside space-y-1">
                      {crossFieldWarnings.filter((w) => !w.blocking).map((w) => (
                        <li key={w.id}>{w.message}</li>
                      ))}
                    </ul>
                    <p className="text-xs text-amber-700 mt-2">
                      These are notes, not errors. You can continue and submit without changing anything.
                    </p>
                  </div>
                </div>
              )}

              {/* De-facto 12-month eligibility block — surfaces s5CB before
                  the form is marked complete so the client doesn't keep
                  pushing forward toward a doomed application. Form data is
                  already saved by autosave, so they can correct the date /
                  add a registered relationship cert and re-submit. */}
              {deFactoBlock && (
                <div className="section">
                  <div className="rounded-lg bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900">
                    <p className="font-semibold mb-2">We need to check one more thing before lodgement</p>
                    <p className="mb-3 leading-relaxed">{deFactoBlock}</p>
                    <p className="text-xs text-amber-800 mb-1 font-semibold">Two ways to satisfy this:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs text-amber-800">
                      <li>Have you been living together for 12 months or more? Confirm the start date in the Relationship section.</li>
                      <li>Have you registered your relationship with a state government in Australia (e.g. NSW Births, Deaths and Marriages)? Upload the certificate under the Documents tab.</li>
                      <li>Otherwise, give us details of any compelling and compassionate circumstances we should put forward (the field is in the Relationship section).</li>
                    </ul>
                    <p className="text-xs text-amber-800 mt-3">Your answers are saved. You won't lose progress. Speak to your case manager if you're not sure which path applies to you.</p>
                  </div>
                </div>
              )}

              {/* Navigation footer */}
              <div className="section flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setCurrentSectionIndex(
                      [...visibleSectionIndices].reverse().find((v) => v < currentSectionIndex) ??
                        currentSectionIndex,
                    )
                  }
                  disabled={currentSectionIndex <= firstVisibleIndex}
                  className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ArrowLeft size={16} /> Back
                </button>

                <div className="text-xs text-slate-400 flex items-center gap-1.5">
                  <Info size={12} /> {isPreview ? 'Editing as the client. Changes save to their form and are logged.' : 'Your answers save automatically.'}
                </div>

                {!isLastSection ? (
                  <button type="button" onClick={handleNext} className="btn-primary">
                    Next <ArrowRight size={16} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitStatus === 'submitting' || isPreview}
                    title={isPreview ? 'Only the client can submit: submitting signs their declaration' : undefined}
                    className="btn-accent disabled:opacity-60"
                  >
                    {submitStatus === 'submitting' ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Submitting...
                      </>
                    ) : (
                      <>
                        Submit <Check size={16} />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Footer note */}
            <div className="mt-4 text-xs text-slate-500 flex items-start gap-2 px-1">
              <CircleAlert size={14} className="mt-0.5 flex-shrink-0 text-slate-400" />
              <span>
                You can skip any question you're unsure about. Our team will follow up on anything
                critical before we start drafting your application. Partial answers are fine.
              </span>
            </div>
          </div>
        </div>
      </form>

      {/* Toast notifications */}
      {saveStatus === 'saved' && (
        <div
          role="status"
          className="fixed bottom-6 right-6 bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg shadow-md text-sm flex items-center gap-2"
        >
          <Check size={14} /> Saved
        </div>
      )}
      {saveStatus === 'error' && (
        <div
          role="alert"
          className="fixed bottom-6 right-6 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg shadow-md text-sm"
        >
          Couldn't save. Check your connection and try again.
        </div>
      )}
    </FormProvider>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface SectionStatusIconProps {
  progress: SectionProgress | undefined;
  isCurrent: boolean;
  index: number;
}

function SectionStatusIcon({ progress, isCurrent, index }: SectionStatusIconProps) {
  if (!progress) {
    return (
      <span
        className={`w-5 h-5 rounded-full border grid place-items-center text-xs font-semibold ${
          isCurrent ? 'border-[#2CABEA] text-[#2CABEA]' : 'border-slate-300 text-slate-400'
        }`}
      >
        {index + 1}
      </span>
    );
  }

  if (progress.complete && progress.totalRequired > 0) {
    return (
      <span className="w-5 h-5 rounded-full bg-green-500 text-white grid place-items-center">
        <Check size={12} />
      </span>
    );
  }

  if (progress.touched && progress.missingRequired > 0) {
    return (
      <span className="w-5 h-5 rounded-full bg-amber-400 text-white grid place-items-center text-xs font-bold">
        !
      </span>
    );
  }

  return (
    <span
      className={`w-5 h-5 rounded-full border grid place-items-center text-xs font-semibold ${
        isCurrent ? 'border-[#2CABEA] text-[#2CABEA]' : 'border-slate-300 text-slate-400'
      }`}
    >
      {index + 1}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Wrap a single section from the schema into a minimal FormSchema so
 * SchemaRenderer renders only that section. Sections are sorted by order
 * in SchemaRenderer already; wrapping one section avoids re-rendering all
 * sections on every navigation step.
 */
function singleSectionSchema(fullSchema: FormSchema, sectionId: string): FormSchema {
  const section = fullSchema.sections.find((s) => s.section_id === sectionId);
  return {
    ...fullSchema,
    sections: section ? [section] : [],
  };
}

function renderSaveLabel(status: SaveStatus, lastSaved: Date | null): string {
  if (status === 'saving') return 'Saving...';
  if (status === 'error') return 'Save failed. Will retry on next change.';
  if (lastSaved) return `Saved ${formatRelative(lastSaved)}`;
  return 'Autosaves as you type';
}

function formatRelative(date: Date): string {
  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
